import { faqsPl } from "@data/flutter-apps-landing/faq";
import {
  aboutUsCopyPl,
  contactFormPl,
  faqStructuredData,
  mobileClusterLinks,
  mobileKnowledgeBaseLinks,
  organizationStructuredData,
  serviceStructuredData,
} from "@data/mobile-web-landing/shared";
import {
  Calendar,
  CheckCircle,
  Code,
  FileText,
  Layers,
  LifeBuoy,
  PenTool,
  Rocket,
  Search,
  Smartphone,
  TabletSmartphone,
  Wrench,
} from "lucide-react";

import type { FAQCopy } from "@components/bluetooth-landing/FAQ";
import type { FooterCopy } from "@components/bluetooth-landing/Footer";
import type { HeroCopy } from "@components/bluetooth-landing/Hero";
import type { ProcessCopy } from "@components/bluetooth-landing/Process";
import type { ServicesCopy } from "@components/bluetooth-landing/Services";
import type { TargetAudienceCopy } from "@components/bluetooth-landing/TargetAudience";
import type { ValuePropositionCopy } from "@components/bluetooth-landing/ValueProposition";

export const title =
  "Aplikacje Flutter - jedna aplikacja na Android i iOS";

export const description =
  "Aplikacje Flutter na Android i iOS — cross-platform MVP, audyt jakości i utrzymanie. Polski software house: jeden codebase, backend i panel. NDA przed wyceną.";

export const structuredData = [
  organizationStructuredData(description),
  serviceStructuredData(
    "Aplikacje Flutter Android i iOS",
    description,
    "Flutter mobile application development",
  ),
  faqStructuredData(faqsPl),
];

export const heroCopy: HeroCopy = {
  header: "Aplikacje Flutter",
  headerTail: "Android i iOS",
  description:
    "Budujemy aplikacje Flutter — jeden codebase na Android i iOS. Od MVP z logowaniem i powiadomieniami, przez audyt istniejącego kodu, po utrzymanie i rozwój z panelem CMS.",
  cta: {
    text: "Darmowa konsultacja",
    href: "#kontakt",
  },
  secondaryCta: {
    text: "Poznaj nasz proces",
    href: "#proces",
  },
  benefits: [
    { text: "Jeden kod — Android i iOS", Icon: CheckCircle },
    { text: "Audyt i przejęcie Flutter", Icon: CheckCircle },
    { text: "MVP + backend / CMS", Icon: CheckCircle },
  ],
};

export const targetAudienceCopy: TargetAudienceCopy = {
  header: {
    h2: "Kim są nasi klienci?",
    h3: "Dla kogo są aplikacje Flutter?",
    p: "Flutter wybierają firmy, które chcą MVP na obie platformy, audyt jakości przed dalszym rozwojem albo utrzymanie po innym wykonawcy. Czy to brzmi jak Twoja sytuacja?",
  },
  points: [
    {
      Icon: Rocket,
      title: "Chcesz MVP na Android i iOS bez dwóch natywnych zespołów",
      description:
        "Jeden stack Flutter skraca time-to-market i budżet — szczególnie gdy zakres to logowanie, dashboard, powiadomienia i podstawowe integracje.",
    },
    {
      Icon: Search,
      title: "Masz aplikację Flutter i potrzebujesz audytu jakości",
      description:
        "Chcesz sprawdzić stabilność, dług techniczny i gotowość do utrzymania zanim zainwestujesz w kolejne funkcje albo zmienisz dostawcę.",
    },
    {
      Icon: Wrench,
      title: "Szukasz firmy do przejęcia i utrzymania Flutter + CMS",
      description:
        "Poprzedni wykonawca odchodzi — potrzebujesz NDA, audytu repozytorium, wyceny utrzymania i planowanego rozwoju bez przestoju produktu.",
    },
    {
      Icon: Layers,
      title: "Budujesz aplikację z backendem, nie sam UI",
      description:
        "Flutter idzie w pakiecie z API, panelem admina albo Firebase/AWS — traktujemy to jako jeden produkt, nie osobne zlecenia.",
    },
    {
      Icon: TabletSmartphone,
      title: "Masz urządzenie BLE i potrzebujesz companion app",
      description:
        "Jeśli komunikacja Bluetooth jest rdzeniem produktu, zobacz też nasz klaster BLE — Flutter często jest warstwą mobilną przy hardware.",
    },
  ],
  cta: {
    h4: "Brzmi znajomo?",
    text: "Opowiedz nam o aplikacji Flutter — nowej albo istniejącej. Zaproponujemy MVP, audyt albo zakres utrzymania.",
    href: "#kontakt",
    buttonText: "Umów darmową konsultację",
  },
};

export const valuePropositionCopy: ValuePropositionCopy = {
  header: {
    h2: "Nasza specjalizacja",
    h3: "Dlaczego warto budować aplikację Flutter z nami?",
    p: "Cross-platform ma sens, gdy liczy się jeden zespół, przewidywalne utrzymanie i jakość kodu gotowa na kolejne miesiące rozwoju — nie tylko szybkie demo.",
    imageAlt: "Aplikacje Flutter na Android i iOS",
  },
  values: [
    {
      Icon: Smartphone,
      title: "Jeden codebase — dwie platformy",
      description:
        "Android i iOS z jednego projektu Flutter: wspólna logika biznesowa, spójny UX i krótszy cykl wydań store.",
    },
    {
      Icon: Search,
      title: "Audyt przed dalszym rozwojem",
      description:
        "Ocena jakości, stabilności i długu technicznego — standardowy krok przed wyceną utrzymania albo przejęciem projektu.",
    },
    {
      Icon: Layers,
      title: "Flutter + backend w jednym zakresie",
      description:
        "API, CMS, powiadomienia push i panel admina projektujemy razem z aplikacją — bo produkt rzadko kończy się na samym UI.",
    },
    {
      Icon: LifeBuoy,
      title: "Utrzymanie po MVP i po innym wykonawcy",
      description:
        "Monitoring crashy, aktualizacje SDK, poprawki store i rozwój funkcji w modelu godzinowym lub SLA.",
    },
    {
      Icon: CheckCircle,
      title: "NDA i przejrzysta wycena",
      description:
        "Podpisujemy NDA przed audytem kodu. Dostajesz orientacyjną wycenę MVP albo utrzymania — bez pustego „to zależy”.",
    },
  ],
};

export const servicesCopy: ServicesCopy = {
  header: {
    h2: "Usługi",
    h3: "Aplikacje Flutter — co budujemy",
    p: "Od nowego MVP po audyt i utrzymanie istniejącej aplikacji. Zakres dopasowujemy do etapu produktu i stacku backendowego.",
  },
  services: [
    {
      Icon: Search,
      title: "Warsztaty i zakres MVP Flutter",
      description:
        "Mapujemy user stories, platformy, integracje i ryzyka. Otrzymujesz plan MVP z widełkami kosztów i harmonogramem.",
    },
    {
      Icon: Smartphone,
      title: "Aplikacja Flutter na Android i iOS",
      description:
        "Projekt UX, implementacja, budowa release'ów store, logowanie, powiadomienia i kluczowe ekrany produktowe.",
    },
    {
      Icon: Layers,
      title: "Integracja z backendem i CMS",
      description:
        "Podłączamy Flutter do API (Node, Firebase, AWS) oraz panelu administracyjnego / CMS — jeden spójny produkt.",
    },
    {
      Icon: Search,
      title: "Audyt aplikacji Flutter",
      description:
        "Jakość kodu, wydajność, architektura, zależności i gotowość do utrzymania — raport z rekomendacjami.",
    },
    {
      Icon: Wrench,
      title: "Przejęcie projektu po innym wykonawcy",
      description:
        "NDA, dostęp do repo, dokumentacja przekazania, stabilizacja buildów i plan dalszego developmentu.",
    },
    {
      Icon: LifeBuoy,
      title: "Utrzymanie i rozwój",
      description:
        "SLA, monitoring, aktualizacje platform, nowe funkcje i wsparcie po publikacji w App Store i Google Play.",
    },
  ],
  cta: {
    h4: "Nie wiesz, od czego zacząć?",
    p: "Napisz, czy budujesz nowe MVP, czy przejmujesz istniejącą aplikację Flutter — zaproponujemy pierwszy krok.",
    button: {
      text: "Umów darmową konsultację",
      href: "#kontakt",
    },
  },
};

export const processCopy: ProcessCopy = {
  header: {
    h2: "Nasz Proces",
    h3: "Jak powstaje aplikacja Flutter",
    p: "Od pierwszej rozmowy i NDA po publikację w store i utrzymanie. Przejrzyste etapy — także przy audycie i przejęciu kodu.",
  },
  stepNumber: "Krok",
  steps: [
    {
      Icon: Calendar,
      title: "Darmowe spotkanie online",
      duration: "w kilka godzin",
      description:
        "30 minut z inżynierem. Opisujesz produkt, platformy i ograniczenia budżetowe — dostajesz pierwszą ocenę zakresu.",
    },
    {
      Icon: FileText,
      title: "Brief, warsztaty lub audyt",
      duration: "w kilka dni",
      description:
        "Dla nowego produktu: user stories i zakres MVP. Dla istniejącego: NDA + audyt repozytorium Flutter.",
    },
    {
      Icon: PenTool,
      title: "Wycena i umowa",
      duration: "ustalenie harmonogramu",
      description:
        "Orientacyjna wycena fixed price lub T&M, kamienie milowe i kryteria akceptacji MVP albo utrzymania.",
    },
    {
      Icon: Code,
      title: "Implementacja Flutter",
      duration: "min. 6–12 tygodni",
      description:
        "Budujemy aplikację, integrujemy API i przygotowujemy buildy testowe na Android i iOS.",
    },
    {
      Icon: Smartphone,
      title: "Testy i publikacja store",
      duration: "2–4 tygodnie",
      description:
        "QA, poprawki UX, przygotowanie listingów i proces review App Store / Google Play.",
    },
    {
      Icon: CheckCircle,
      title: "Utrzymanie i kolejne iteracje",
      duration: "na stałe",
      description:
        "Monitoring, aktualizacje SDK i rozwój funkcji — albo przekazanie z dokumentacją do Waszego zespołu.",
    },
  ],
  cta: {
    href: "#kontakt",
    text: "Wyceń aplikację Flutter",
  },
};

export const aboutUsCopy = aboutUsCopyPl({
  h3: "Polski zespół aplikacji Flutter i mobile",
  imageAlt: "Zespół Blues Brackets — aplikacje Flutter Android i iOS",
});

export const faqCopy: FAQCopy = {
  header: {
    h2: "FAQ",
    h3: "Najczęściej zadawane pytania o aplikacje Flutter",
  },
  faqs: faqsPl,
};

export const footerCopy: FooterCopy = {
  header: {
    h2: "Planujesz aplikację Flutter?",
    p: "Umów się na darmową konsultację — opowiedz o MVP, audycie albo utrzymaniu, a zaproponujemy zakres i orientacyjną wycenę.",
  },
  cta: {
    href: "#kontakt",
    text: "Umów darmową konsultację",
    p: "Aplikacje Flutter na Android i iOS — MVP, audyt i utrzymanie. Polski software house.",
    email: "hello@bluesbrackets.com",
    phone: "+48 535 462 678",
  },
  links: {
    h4: "Na skróty",
    links: [
      { href: "#uslugi", text: "Nasze usługi" },
      { href: "#proces", text: "Proces współpracy" },
      { href: "#faq", text: "FAQ" },
      {
        href: "/aplikacje-mobilne-i-webowe",
        text: "Aplikacje mobilne i webowe",
      },
      ...mobileClusterLinks.filter(
        l =>
          l.href !== "/aplikacje-mobilne-i-webowe" &&
          l.href !== "/aplikacje-flutter-android-ios",
      ),
    ],
  },
  knowledgeBase: {
    h4: "Baza wiedzy",
    links: [...mobileKnowledgeBaseLinks],
  },
  copyright: "bluesBrackets. Wszelkie prawa zastrzeżone.",
  privacyPolicy: "Polityka prywatności",
  privacyPolicyHref: "/policy",
  contactForm: contactFormPl,
};
